import React from 'react';
import { View, TextInput, StyleSheet, Platform, TextInputProps, TouchableOpacity } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { colors, typographyTokens } from '../../styles';
import { Typography } from './Typography';
import { useTheme } from '../../context/ThemeContext';

interface InputProps extends TextInputProps {
    label?: string;
    error?: string;
    icon?: keyof typeof Ionicons.glyphMap;
    rightIcon?: keyof typeof Ionicons.glyphMap;
    onRightIconPress?: () => void;
    containerStyle?: any;
}

export const Input: React.FC<InputProps> = ({
    label,
    error,
    icon,
    rightIcon,
    onRightIconPress,
    style,
    containerStyle,
    multiline,
    ...props
}) => {
    const { colors: themeColors, isDark } = useTheme();

    return (
        <View style={[styles.container, containerStyle]}>
            {label && (
                <Typography variant="label" color={themeColors.textMuted} style={styles.label}>
                    {label}
                </Typography>
            )}
            <View style={[
                styles.inputWrapper,
                { backgroundColor: themeColors.canvas, borderColor: isDark ? themeColors.border : colors.slate200 },
                multiline && styles.inputWrapperMultiline,
                error ? styles.inputWrapperError : null,
                props.editable === false ? [styles.inputWrapperDisabled, isDark && { backgroundColor: themeColors.surfaceHover }] : null,
            ]}>
                {icon && (
                    <Ionicons name={icon} size={20} color={themeColors.textMuted} style={[styles.icon, multiline && { marginTop: 14 }]} />
                )}
                <TextInput
                    style={[
                        styles.input,
                        { color: themeColors.textPrimary },
                        multiline && styles.inputMultiline,
                        // Reserve room on the right when the absolute-positioned
                        // eye icon is rendered, so password dots / typed text
                        // don't slide underneath it.
                        rightIcon && { paddingRight: 32 },
                        style,
                    ]}
                    placeholderTextColor={themeColors.textMuted}
                    multiline={multiline}
                    textAlignVertical={multiline ? 'top' : 'center'}
                    {...props}
                />
                {rightIcon && (
                    <TouchableOpacity onPress={onRightIconPress} disabled={!onRightIconPress} style={styles.rightIcon}>
                        <Ionicons name={rightIcon} size={20} color={themeColors.textMuted} />
                    </TouchableOpacity>
                )}
            </View>
            {error && (
                <Typography variant="error" accessibilityLiveRegion="polite" color={themeColors.error} style={styles.errorText}>
                    {error}
                </Typography>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        width: '100%',
        marginBottom: 16,
    },
    label: {
        marginBottom: 8,
    },
    inputWrapper: {
        position: 'relative',
        flexDirection: 'row',
        alignItems: 'center',
        height: 52,
        borderWidth: 1,
        borderColor: colors.slate200,
        borderRadius: 12,
        backgroundColor: colors.white,
        paddingHorizontal: 16,
        ...(Platform.OS === 'web' && {
            transition: 'all 0.2s ease',
            // Focus outline removed (owner request): no solar border / orange glow
            // on focus — the field keeps its neutral border. Web-only; native has
            // no focus-within. Error state still recolours the border (red).
        }),
    } as any,
    inputWrapperMultiline: {
        height: 'auto',
        minHeight: 120,
        alignItems: 'flex-start',
        paddingVertical: 12,
    },
    inputWrapperError: {
        borderColor: colors.red600,
    },
    inputWrapperDisabled: {
        backgroundColor: colors.slate50,
        opacity: 0.7,
    },
    icon: {
        marginRight: 12,
    },
    input: {
        flex: 1,
        height: '100%',
        ...typographyTokens.input,
        color: colors.slate900,
        ...(Platform.OS === 'web' && {
            // On web RN-Web renders TextInput as an HTML <input>. Browser
            // defaults give it its own background, 1px solid border and
            // borderRadius — which look like an inner «bordered box» that
            // competes with our `inputWrapper`. Result on dark mobile:
            // the wrapper border is faint, the inner <input> looks like
            // the «real» field, and the right-side eye icon (which is a
            // sibling of <input> inside the wrapper) appears OUTSIDE it.
            // Force the <input> to be a transparent text surface — the
            // wrapper draws the bordered pill, icons sit naturally inside.
            outlineStyle: 'none',
            backgroundColor: 'transparent',
            borderWidth: 0,
            borderRadius: 0,
        }),
    } as any,
    inputMultiline: {
        height: 'auto',
        minHeight: 96,
        paddingTop: 4,
    },
    rightIcon: {
        position: 'absolute',
        right: 16,
        top: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
    },
    errorText: {
        marginTop: 4,
    },
});
